import { FormControl, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProjectModalTypes } from '../../../../../core/legacy/reducers/modals';
import apiEndpoint from '../../../../../core/legacy/utils/api';
import editProjectSections from '../../../../../core/project/commands/EditProjectSections';
import { RepairSolution } from '../../../../../core/project/ExternalProjectPort';
import { currentProjectIdQuery, selectedProjectSectionsQuery } from '../../../../../core/project/queries';
import { useAppDispatch, useAppSelector } from '../../../../../types';
import GenericModal from '../../../../legacy/components/ui/modals/GenericModal';
import { ContentContainer, ModalTitle } from '../../../../legacy/components/ui/modals/styles';
import { useProjectSnackbar } from '../../ProjectSnackbarContext';

const EMPTY_VALUE = '';

const EditProjectSectionRepairSolutionModal = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const checkedProjectSections = useAppSelector(selectedProjectSectionsQuery);
    const checkedProjectSectionsIds = checkedProjectSections.map((s) => s.id);
    const currentProjectId = useAppSelector(currentProjectIdQuery);
    const { setSnackbarMessage } = useProjectSnackbar();

    const [repairSolution, setRepairSolution] = useState<string>(EMPTY_VALUE);
    const [repairSolutions, setRepairSolutions] = useState<RepairSolution[]>([]);

    useEffect(() => {
        const loadRepairSolutions = async () => {
            try {
                const response = await axios.request({
                    method: 'GET',
                    url: `${apiEndpoint}/project/repair-solutions`,
                    withCredentials: true,
                });
                setRepairSolutions(response.data);
            } catch (err) {
                console.error('Error loading repair solutions:', err);
                setRepairSolutions([]);
            }
        };
        loadRepairSolutions();
    }, []);

    useEffect(() => {
        const selectedRepairSolutions = checkedProjectSections
            .map((ps) => ps.repairSolution)
            .filter((rs) => rs !== undefined && rs !== null)
            .reduce((acc, val) => acc.add(val!), new Set<string>());
        if (selectedRepairSolutions.size === 1) {
            setRepairSolution(selectedRepairSolutions.values().next().value as string);
        } else {
            setRepairSolution(EMPTY_VALUE);
        }
    }, [checkedProjectSections]);

    const onValidate = async () => {
        if (!currentProjectId) {
            setSnackbarMessage(t('projectList.error.noProjectSelected'));

            return;
        }

        const normalizedValue = repairSolution === EMPTY_VALUE ? null : repairSolution;
        const repairSolutionCosts: Record<string, number> = repairSolutions.reduce((acc, solution) => {
            acc[solution.name] = solution.cost;

            return acc;
        }, {} as Record<string, number>);
        const conditionalCost = normalizedValue ? repairSolutionCosts[normalizedValue] || null : null;

        try {
            await dispatch(
                editProjectSections({
                    projectSectionData: {
                        repairSolution: normalizedValue,
                        conditionalCost,
                    },
                    projectSectionIds: checkedProjectSectionsIds,
                }),
            ).unwrap();
            setRepairSolution(EMPTY_VALUE);
        } catch (err) {
            console.error('Error editing project sections:', err);
            setSnackbarMessage(t('projectList.error.default'));
        }
    };

    const handleChange = (event: SelectChangeEvent<string>) => {
        setRepairSolution(event.target.value);
    };

    return (
        <GenericModal
            onValidate={onValidate}
            modalType={ProjectModalTypes.PROJECT_SECTION_EDIT_REPAIR_SOLUTION}
            options={{ notDismissableByClickingOnBackground: true }}
        >
            <ContentContainer>
                <ModalTitle>{t('projectList.modals.editRepairSolution')}</ModalTitle>
                <FormControl fullWidth variant="standard">
                    <Select value={repairSolution} onChange={handleChange} autoFocus>
                        <MenuItem value={EMPTY_VALUE}>-</MenuItem>
                        {repairSolutions.map((solution) => (
                            <MenuItem key={`${solution.name}-${solution.cost}`} value={solution.name}>
                                {solution.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </ContentContainer>
        </GenericModal>
    );
};

export default EditProjectSectionRepairSolutionModal;
